import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Fuel, LayoutDashboard, ClipboardCheck, Building2, Users, Truck, PlusCircle, History, LogOut, Menu, X, Upload, ShieldAlert, KeyRound, CreditCard, Inbox, FolderOpen, Sparkles, Smartphone, ListChecks, Ticket, PanelLeftClose, PanelLeftOpen, CircleDot, TrendingUp, FileSignature, Droplets, FlaskConical } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { canSee } from "@/lib/sections";

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/admin/controllo", label: "Dati per stazione", icon: ClipboardCheck, id: "control" },
  { to: "/admin/stazioni", label: "Stazioni", icon: Building2, id: "stations" },
  { to: "/admin/utenti", label: "Utenti", icon: Users, id: "users" },
  { to: "/admin/assegnazioni", label: "Assegnazioni", icon: ListChecks, id: "assignments" },
  { to: "/admin/flotta", label: "Disponibilità furgoni", icon: Truck, id: "fleet" },
  { to: "/admin/franchigie", label: "Sinistri e franchigie", icon: ShieldAlert, id: "damages" },
  { to: "/admin/noleggi", label: "Noleggi", icon: KeyRound, id: "rentals" },
  { to: "/admin/telepass", label: "Telepass", icon: CreditCard, id: "telepass" },
  { to: "/admin/telefoni", label: "Telefoni", icon: Smartphone, id: "phones" },
  { to: "/admin/richieste", label: "Richieste", icon: Inbox, id: "requests" },
  { to: "/admin/documenti", label: "Documenti", icon: FolderOpen, id: "documents" },
  { to: "/admin/pedaggi", label: "Mancati pedaggi", icon: Ticket, id: "tolls" },
  { to: "/admin/gomme", label: "Gomme", icon: CircleDot, id: "tyres" },
  { to: "/admin/forecast", label: "Forecast furgoni", icon: TrendingUp, id: "forecast" },
  { to: "/admin/moduli", label: "Moduli", icon: FileSignature, id: "forms" },
  { to: "/admin/lavaggi", label: "Lavaggi", icon: Droplets, id: "washes" },
  { to: "/admin/adblue", label: "Magazzino AdBlue", icon: FlaskConical, id: "adblue" },
  { to: "/admin/importa", label: "Importa Excel", icon: Upload, id: "import" },
  { to: "/admin/assistente", label: "Assistente AI", icon: Sparkles, id: "ai" },
];
const OP_NAV = [
  { to: "/stazione", label: "Inserimento giornaliero", icon: PlusCircle, end: true, id: "entry" },
  { to: "/stazione/storico", label: "Storico e riepilogo", icon: History, id: "history" },
  { to: "/stazione/assegnazioni", label: "Assegnazioni", icon: ListChecks, id: "assignments" },
  { to: "/stazione/flotta", label: "Disponibilità furgoni", icon: Truck, id: "fleet" },
  { to: "/stazione/franchigie", label: "Sinistri e franchigie", icon: ShieldAlert, id: "damages" },
  { to: "/stazione/noleggi", label: "Noleggi", icon: KeyRound, id: "rentals" },
  { to: "/stazione/telepass", label: "Telepass", icon: CreditCard, id: "telepass" },
  { to: "/stazione/telefoni", label: "Telefoni", icon: Smartphone, id: "phones" },
  { to: "/stazione/richieste", label: "Richieste", icon: Inbox, id: "requests" },
  { to: "/stazione/documenti", label: "Documenti", icon: FolderOpen, id: "documents" },
  { to: "/stazione/pedaggi", label: "Mancati pedaggi", icon: Ticket, id: "tolls" },
  { to: "/stazione/gomme", label: "Gomme", icon: CircleDot, id: "tyres" },
  { to: "/stazione/forecast", label: "Forecast furgoni", icon: TrendingUp, id: "forecast" },
  { to: "/stazione/moduli", label: "Moduli", icon: FileSignature, id: "forms" },
  { to: "/stazione/lavaggi", label: "Lavaggi", icon: Droplets, id: "washes" },
  { to: "/stazione/adblue", label: "Magazzino AdBlue", icon: FlaskConical, id: "adblue" },
];

const NavItems = ({ items, onClick, mini }) => (
  <nav className="flex flex-col gap-1">
    {items.map(({ to, label, icon: Icon, end, id }) => (
      <NavLink key={to} to={to} end={end} onClick={onClick} data-testid={`nav-${id}`} title={mini ? label : undefined}
        className={({ isActive }) => `flex items-center gap-3 rounded-lg py-2.5 text-sm font-medium transition-colors ${mini ? "justify-center px-2" : "px-3"} ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
        <Icon className="h-4 w-4 shrink-0" /> {!mini && <span className="truncate">{label}</span>}
      </NavLink>
    ))}
  </nav>
);

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const path = useLocation().pathname;
  const wide = path.startsWith("/admin/controllo") || path.endsWith("/flotta") || path.endsWith("/franchigie") || path.endsWith("/noleggi") || path.endsWith("/telepass") || path.endsWith("/telefoni") || path.endsWith("/assegnazioni") || path.endsWith("/pedaggi") || path.endsWith("/richieste") || path.endsWith("/documenti") || path.endsWith("/assistente");
  const [open, setOpen] = useState(false);
  const [mini, setMini] = useState(() => localStorage.getItem("ff_nav_mini") === "1");
  const toggleMini = () => setMini((m) => { localStorage.setItem("ff_nav_mini", m ? "0" : "1"); return !m; });
  const [stationName, setStationName] = useState("");
  const isAdmin = user.role === "admin";

  useEffect(() => {
    if (!isAdmin) api.get("/stations").then((r) => setStationName(r.data[0]?.name || "")).catch(() => {});
  }, [isAdmin]);

  const doLogout = () => { logout(); navigate("/login"); };

  const items = isAdmin ? ADMIN_NAV.filter((n) => (!user.supervisor || !["users", "import", "ai"].includes(n.id)) && canSee(user, n.id)) : OP_NAV.filter((n) => canSee(user, n.id));
  const sidebar = (m) => (
    <div className={`flex h-full flex-col bg-slate-900 ${m ? "px-2 py-5" : "p-5"}`}>
      <div className={`mb-6 flex shrink-0 items-center gap-3 ${m ? "flex-col" : ""}`}>
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-600"><Fuel className="h-5 w-5 text-white" /></div>
        {!m && <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-white leading-tight">FleetFuel</p>
          <p className="text-[11px] uppercase tracking-wider text-slate-400">{isAdmin ? "Amministrazione" : "Stazione"}</p>
        </div>}
        <button onClick={toggleMini} title={m ? "Allarga menu" : "Riduci menu"} data-testid="sidebar-collapse-button"
          className="hidden rounded-md p-1.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white lg:block">
          {m ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </div>
      <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 [scrollbar-width:thin]" data-testid="sidebar-scroll">
        <NavItems items={items} mini={m} onClick={() => setOpen(false)} />
      </div>
      <div className="mt-4 shrink-0 border-t border-white/10 pt-4">
        {!m && <>
          <p className="truncate text-sm font-semibold text-white" data-testid="current-user-name">{user.name}</p>
          <p className="truncate text-xs text-slate-400">{isAdmin ? (user.supervisor ? "Supervisore · sola visione" : user.office ? "Amministrativo" : "Amministratore") : stationName}</p>
        </>}
        <button onClick={doLogout} data-testid="logout-button" title="Esci"
          className={`mt-3 flex w-full items-center gap-2 rounded-lg py-2 text-sm text-slate-400 hover:bg-white/5 hover:text-white transition-colors ${m ? "justify-center px-2" : "px-3"}`}>
          <LogOut className="h-4 w-4" /> {!m && "Esci"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className={`fixed inset-y-0 left-0 z-30 hidden transition-[width] duration-200 lg:block ${mini ? "w-16" : "w-64"}`} data-testid="sidebar">{sidebar(mini)}</aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64">{sidebar(false)}</aside>
        </div>
      )}
      <div className={`transition-[padding] duration-200 ${mini ? "lg:pl-16" : "lg:pl-64"}`}>
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b bg-white/80 px-4 backdrop-blur lg:hidden">
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-button" className="p-2">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
          <span className="text-sm font-bold">FleetFuel</span>
          <span className="w-9" />
        </header>
        <main className={`mx-auto p-4 sm:p-6 lg:p-8 ${wide ? "max-w-none 2xl:px-10" : "max-w-[1680px]"} ${user.supervisor ? "read-only" : ""}`}>
          {user.supervisor && <p className="mb-4 inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="readonly-badge">Sola visione</p>}
          {children}
        </main>
      </div>
    </div>
  );
}
